// Insured-limit helpers, derived from a tenant's insurance requests.
//
// A company's "insured limit" is the cover limit of its active insurance policy — i.e. a request
// that (a) is not withdrawn/declined and (b) names a carrier. When no carrier is connected for a
// company we return null so the UI can render "-". Whole-turnover cover applies book-wide, so it
// contributes a floor limit to every buyer.

const INACTIVE = new Set(["withdrawn", "declined"]);
const WHOLE = "__WHOLE_TURNOVER__";

const limitOf = (r) => Number(r.coverLimit ?? r.approvedLimit ?? r.requestedLimit) || 0;

/**
 * Build a companyId -> insured-limit map from a list of insurance requests.
 * Only requests with a connected carrier (non-null) that are still active are counted.
 * @param {Array} requests
 * @returns {Map<string, number>}
 */
export function buildInsuredLimitMap(requests = []) {
  const map = new Map();
  for (const r of requests || []) {
    if (!r || INACTIVE.has(r.status)) continue;
    if (!r.carrier) continue; // carrier not connected → not insured
    const lim = limitOf(r);
    if (lim <= 0) continue;
    if (r.product === "whole_turnover") {
      map.set(WHOLE, Math.max(map.get(WHOLE) || 0, lim));
      continue;
    }
    if (!r.companyId) continue;
    map.set(r.companyId, Math.max(map.get(r.companyId) || 0, lim));
  }
  return map;
}

/**
 * The insured limit for a single company, or null when no carrier is connected.
 * @param {Map<string, number>|null} map
 * @param {string} companyId
 * @returns {number|null}
 */
export function insuredLimitFor(map, companyId) {
  if (!map) return null;
  const book = map.get(WHOLE) || 0;
  const own = (companyId && map.get(companyId)) || 0;
  const eff = Math.max(own, book);
  return eff > 0 ? eff : null;
}
